import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  checkAuth,
  clearAuthState,
  initializeFromStorage,
} from "../../store/slices/authSlice";
import { AnimatePresence, motion } from "framer-motion";
import { Shield, Loader2, AlertCircle, RefreshCw, LogOut } from "lucide-react";

const GuardRoute = ({ children, requireAdmin = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [showForceLogout, setShowForceLogout] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  const { isAuthenticated, isLoading, isAdmin, error, isOffline } = useSelector(
    (state) => state.auth
  );

  // Initialize auth from storage
  useEffect(() => {
    dispatch(initializeFromStorage());
  }, [dispatch]);

  useEffect(() => {
    if (isLoading || isAuthenticated) return;

    const timer = setTimeout(() => {
      if (!isAuthenticated && !isLoading) {
        dispatch(checkAuth());
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [dispatch, isAuthenticated, isLoading, location.pathname]);

  useEffect(() => {
    if (!isLoading && !initialCheckDone) {
      setInitialCheckDone(true);
    }
  }, [isLoading, initialCheckDone]);

  const handleRetry = () => {
    dispatch(checkAuth());
  };

  const handleForceLogout = () => {
    dispatch(clearAuthState());
    navigate("/login", { replace: true });
  };

  // Show loading only on initial check
  if (isLoading && !initialCheckDone) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ background: "var(--bg-primary)" }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="relative">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center bg-gradient-to-r from-primary-500 to-primary-700">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <Loader2 className="absolute inset-0 w-20 h-20 mx-auto animate-spin text-primary-300" />
          </div>
          <h2 className="text-xl font-bold mb-3 text-primary">
            جاري التحقق من الصلاحيات...
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            يرجى الانتظار أثناء التحقق من بيانات الدخول
          </p>
        </motion.div>
      </div>
    );
  }

  // Redirect to login
  if (!isAuthenticated && initialCheckDone) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check admin privileges
  if (requireAdmin && !isAdmin && isAuthenticated) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ background: "var(--bg-primary)" }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md mx-auto p-8 rounded-2xl"
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-primary)",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 flex items-center justify-center">
            <Shield className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-primary">
            غير مصرح بالوصول
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            ليس لديك صلاحيات كافية للوصول إلى هذه الصفحة
          </p>
          <motion.button
            onClick={() => navigate("/admin")}
            className="w-full py-3 rounded-xl font-medium bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-md hover:shadow-lg"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            العودة للوحة التحكم
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Show session expired/error screen
  if (error && isAuthenticated) {
    const isSessionError =
      error.includes("Session expired") ||
      error.includes("No authentication cookies") ||
      error.includes("Authentication cookies expired");

    if (isSessionError) {
      return (
        <div
          className="flex items-center justify-center min-h-screen"
          style={{ background: "var(--bg-primary)" }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-md mx-auto p-8 rounded-2xl"
            style={{
              background: "var(--bg-card)",
              border: "1px solid var(--border-primary)",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold mb-3 text-primary">
              انتهت صلاحية الجلسة
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <motion.button
                onClick={handleRetry}
                className="flex-1 py-3 rounded-xl font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <RefreshCw className="w-4 h-4" />
                إعادة المحاولة
              </motion.button>
              <motion.button
                onClick={handleForceLogout}
                className="flex-1 py-3 rounded-xl font-medium bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <LogOut className="w-4 h-4" />
                تسجيل الدخول من جديد
              </motion.button>
            </div>
          </motion.div>
        </div>
      );
    }
  }

  // Show offline mode warning (but still allow access)
  if (isOffline && isAuthenticated) {
    return (
      <>
        <div className="fixed top-4 left-4 right-4 z-50">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-center justify-between shadow-lg"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-300">
                  الوضع غير متصل
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {error ||
                    "جاري استخدام بيانات الدخول المخزنة محلياً. قد لا تعمل بعض الميزات."}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <motion.button
                onClick={handleRetry}
                className="px-3 py-1 text-sm bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                إعادة المحاولة
              </motion.button>
              <motion.button
                onClick={() => setShowForceLogout(true)}
                className="px-3 py-1 text-sm bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                تسجيل الخروج
              </motion.button>
            </div>
          </motion.div>
        </div>

        {/* Force Logout Confirmation Modal */}
        <AnimatePresence>
          {showForceLogout && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-[60]"
                onClick={() => setShowForceLogout(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="fixed inset-0 z-[70] flex items-center justify-center p-4"
              >
                <div
                  className="max-w-md w-full rounded-2xl p-6"
                  style={{
                    background: "var(--bg-card)",
                    border: "1px solid var(--border-primary)",
                    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-r from-red-100 to-pink-100 dark:from-red-900/20 dark:to-pink-900/20 flex items-center justify-center">
                    <LogOut className="w-6 h-6 text-red-500" />
                  </div>
                  <h3 className="text-lg font-bold text-center mb-2 text-primary">
                    تأكيد تسجيل الخروج
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
                    هل أنت متأكد أنك تريد تسجيل الخروج؟
                    <br />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      سيتم مسح جميع بيانات الدخول المحفوظة.
                    </span>
                  </p>
                  <div className="flex gap-3">
                    <motion.button
                      onClick={() => setShowForceLogout(false)}
                      className="flex-1 py-3 rounded-xl font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      إلغاء
                    </motion.button>
                    <motion.button
                      onClick={handleForceLogout}
                      className="flex-1 py-3 rounded-xl font-medium bg-gradient-to-r from-red-500 to-pink-500 text-white hover:from-red-600 hover:to-pink-600 transition-all duration-300 shadow-md hover:shadow-lg"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      تسجيل الخروج
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Main content */}
        <div className="pt-20">{children}</div>
      </>
    );
  }

  return children;
};

export default GuardRoute;
